import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Inbox, Send, PenLine } from 'lucide-react';

export type EmailDirectionFilter = 'all' | 'sent' | 'received';

export interface EmailsFilterSidebarProps {
  /** Current direction filter — only applies on `/ats/emails`. */
  activeFilter?: EmailDirectionFilter;
  /** Called when a direction filter is clicked while on `/ats/emails`. */
  onFilterChange?: (filter: EmailDirectionFilter) => void;
}

export function EmailsFilterSidebar({
  activeFilter = 'all',
  onFilterChange,
}: EmailsFilterSidebarProps) {
  const { t } = useTranslation('ats');
  const navigate = useNavigate();
  const location = useLocation();

  const onSignaturesPage = location.pathname.startsWith('/ats/email-signatures');

  const directionItems: { key: EmailDirectionFilter; label: string; icon: typeof Mail }[] = [
    { key: 'all', label: t('email.filterAll'), icon: Mail },
    { key: 'received', label: t('email.filterInbox'), icon: Inbox },
    { key: 'sent', label: t('email.filterSent'), icon: Send },
  ];

  const handleDirectionClick = (key: EmailDirectionFilter) => {
    if (onSignaturesPage) {
      const qs = key === 'all' ? '' : `?direction=${key}`;
      navigate(`/ats/emails${qs}`);
    } else {
      onFilterChange?.(key);
    }
  };

  return (
    <div className="hidden lg:flex lg:flex-col w-52 bg-gray-50 border-r py-3">
      {directionItems.map(({ key, label, icon: Icon }) => {
        const isActive = !onSignaturesPage && activeFilter === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => handleDirectionClick(key)}
            className={`flex items-center gap-3 px-6 py-2.5 mr-3 text-sm transition-colors cursor-pointer ${
              isActive
                ? 'bg-orange-100 text-orange-700 font-semibold rounded-r-full'
                : 'text-gray-700 hover:bg-gray-100 rounded-r-full'
            }`}
          >
            <Icon className="h-4.5 w-4.5 shrink-0" />
            {label}
          </button>
        );
      })}

      <div className="my-2 mx-6 border-t border-gray-200" />

      <button
        type="button"
        onClick={() => navigate('/ats/email-signatures')}
        className={`flex items-center gap-3 px-6 py-2.5 mr-3 text-sm transition-colors cursor-pointer ${
          onSignaturesPage
            ? 'bg-orange-100 text-orange-700 font-semibold rounded-r-full'
            : 'text-gray-700 hover:bg-gray-100 rounded-r-full'
        }`}
      >
        <PenLine className="h-4.5 w-4.5 shrink-0" />
        {t('email.filterSignatures')}
      </button>
    </div>
  );
}
